import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #181818;
  border-radius: 8px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #000;
  }

  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #666;
  }
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: #181818;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: #333;
  }
`;

const ModalBody = styled.div`
  padding: 24px 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #e50914;
  }

  &::placeholder {
    color: #888;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;
  cursor: pointer;

  &:focus {
    border-color: #e50914;
  }

  option {
    background: #2a2a2a;
    color: #fff;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${props => props.$checked ? '#e50914' : '#2a2a2a'};
  border: 1px solid ${props => props.$checked ? '#e50914' : '#444'};
  border-radius: 4px;
  color: #fff;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$checked ? '#c40812' : '#333'};
  }

  input[type="checkbox"] {
    margin: 0;
    cursor: pointer;
  }
`;

const SliderGroup = styled.div`
  margin-top: 8px;
`;

const SliderContainer = styled.div`
  padding: 12px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SliderLabel = styled.span`
  color: #aaa;
  font-size: 0.9rem;
  min-width: 60px;
`;

const Slider = styled.input`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  background: #444;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #e50914;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #e50914;
    cursor: pointer;
    border: none;
  }
`;

const SliderValue = styled.span`
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  min-width: 50px;
  text-align: right;
`;

const ModalFooter = styled.div`
  padding: 24px 32px;
  border-top: 1px solid #333;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background: #181818;
  z-index: 1;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: #e50914;
    color: #fff;

    &:hover {
      background: #c40812;
    }

    &:disabled {
      background: #666;
      cursor: not-allowed;
    }
  ` : `
    background: #333;
    color: #fff;

    &:hover {
      background: #444;
    }
  `}
`;

const HelpText = styled.p`
  color: #aaa;
  font-size: 0.85rem;
  margin: 8px 0 0 0;
  font-style: italic;
`;

const CustomSectionModal = ({ isOpen, onClose, onSave, editingSection }) => {
  const { enabledSources } = useSelector(state => state.settings);
  const { scrapedContent } = useSelector(state => state.content);

  // Extract unique genres from content
  const availableGenres = useMemo(() => {
    const genreSet = new Set();
    if (scrapedContent.movies) {
      scrapedContent.movies.forEach(movie => {
        if (movie.genre && Array.isArray(movie.genre)) {
          movie.genre.forEach(g => genreSet.add(g));
        }
      });
    }
    return Array.from(genreSet).sort();
  }, [scrapedContent.movies]);

  const [formData, setFormData] = useState({
    title: '',
    filters: {
      types: [],
      sources: [],
      genres: [],
      yearRange: { min: 1900, max: new Date().getFullYear() },
      minRating: 0
    },
    sortBy: 'default',
    sortOrder: 'desc'
  });

  // Load editing data when modal opens
  useEffect(() => {
    if (editingSection) {
      setFormData({
        title: editingSection.title || '',
        filters: {
          types: editingSection.filters?.types || [],
          sources: editingSection.filters?.sources || [],
          genres: editingSection.filters?.genres || [],
          yearRange: editingSection.filters?.yearRange || { min: 1900, max: new Date().getFullYear() },
          minRating: editingSection.filters?.minRating || 0
        },
        sortBy: editingSection.sortBy || 'default',
        sortOrder: editingSection.sortOrder || 'desc'
      });
    } else {
      // Reset form for new section
      setFormData({
        title: '',
        filters: {
          types: [],
          sources: [],
          genres: [],
          yearRange: { min: 1900, max: new Date().getFullYear() },
          minRating: 0
        },
        sortBy: 'default',
        sortOrder: 'desc'
      });
    }
  }, [editingSection, isOpen]);

  const handleTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        types: prev.filters.types.includes(type)
          ? prev.filters.types.filter(t => t !== type)
          : [...prev.filters.types, type]
      }
    }));
  };

  const handleSourceToggle = (source) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        sources: prev.filters.sources.includes(source)
          ? prev.filters.sources.filter(s => s !== source)
          : [...prev.filters.sources, source]
      }
    }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        genres: prev.filters.genres.includes(genre)
          ? prev.filters.genres.filter(g => g !== genre)
          : [...prev.filters.genres, genre]
      }
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  const currentYear = new Date().getFullYear();
  const canSubmit = formData.title.trim().length > 0;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {editingSection ? 'Edit Section' : 'Create Custom Section'}
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label htmlFor="section-title">Section Name *</Label>
            <Input
              id="section-title"
              type="text"
              placeholder="e.g., My Action Movies"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
            <HelpText>Give your section a descriptive name</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Content Types</Label>
            <CheckboxGroup>
              <CheckboxLabel $checked={formData.filters.types.includes('movie')}>
                <input
                  type="checkbox"
                  checked={formData.filters.types.includes('movie')}
                  onChange={() => handleTypeToggle('movie')}
                />
                Movies
              </CheckboxLabel>
              <CheckboxLabel $checked={formData.filters.types.includes('tv')}>
                <input
                  type="checkbox"
                  checked={formData.filters.types.includes('tv')}
                  onChange={() => handleTypeToggle('tv')}
                />
                TV Shows
              </CheckboxLabel>
              <CheckboxLabel $checked={formData.filters.types.includes('tv_channel')}>
                <input
                  type="checkbox"
                  checked={formData.filters.types.includes('tv_channel')}
                  onChange={() => handleTypeToggle('tv_channel')}
                />
                Live TV
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>Leave empty to show all types</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Sources</Label>
            <CheckboxGroup>
              {enabledSources.map(source => (
                <CheckboxLabel key={source} $checked={formData.filters.sources.includes(source)}>
                  <input
                    type="checkbox"
                    checked={formData.filters.sources.includes(source)}
                    onChange={() => handleSourceToggle(source)}
                  />
                  {source}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
            <HelpText>Leave empty to show content from all sources</HelpText>
          </FormGroup>

          {availableGenres.length > 0 && (
            <FormGroup>
              <Label>Genres</Label>
              <CheckboxGroup>
                {availableGenres.map(genre => (
                  <CheckboxLabel key={genre} $checked={formData.filters.genres.includes(genre)}>
                    <input
                      type="checkbox"
                      checked={formData.filters.genres.includes(genre)}
                      onChange={() => handleGenreToggle(genre)}
                    />
                    {genre}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
              <HelpText>Leave empty to show all genres</HelpText>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Year Range</Label>
            <SliderGroup>
              <SliderContainer>
                <SliderRow>
                  <SliderLabel>From:</SliderLabel>
                  <Slider
                    type="range"
                    min="1900"
                    max={currentYear}
                    value={formData.filters.yearRange.min}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        yearRange: {
                          ...prev.filters.yearRange,
                          min: parseInt(e.target.value)
                        }
                      }
                    }))}
                  />
                  <SliderValue>{formData.filters.yearRange.min}</SliderValue>
                </SliderRow>
                <SliderRow>
                  <SliderLabel>To:</SliderLabel>
                  <Slider
                    type="range"
                    min="1900"
                    max={currentYear}
                    value={formData.filters.yearRange.max}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        yearRange: {
                          ...prev.filters.yearRange,
                          max: parseInt(e.target.value)
                        }
                      }
                    }))}
                  />
                  <SliderValue>{formData.filters.yearRange.max}</SliderValue>
                </SliderRow>
              </SliderContainer>
            </SliderGroup>
            <HelpText>Filter content by release year</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Minimum Rating</Label>
            <SliderGroup>
              <SliderContainer>
                <SliderRow>
                  <Slider
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={formData.filters.minRating}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        minRating: parseFloat(e.target.value)
                      }
                    }))}
                  />
                  <SliderValue>{formData.filters.minRating.toFixed(1)}</SliderValue>
                </SliderRow>
              </SliderContainer>
            </SliderGroup>
            <HelpText>Show content with IMDb rating of {formData.filters.minRating.toFixed(1)} or higher (0 = no filter)</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="sort-by">Sort By</Label>
            <Select
              id="sort-by"
              value={formData.sortBy}
              onChange={(e) => setFormData(prev => ({ ...prev, sortBy: e.target.value }))}
            >
              <option value="default">Default Order</option>
              <option value="rating">Highest Rated First</option>
              <option value="year">Release Year (Newest First)</option>
              <option value="runtime">Runtime (Longest First)</option>
              <option value="title">Alphabetical (A-Z)</option>
              <option value="popularity">Most Popular First</option>
            </Select>
            <HelpText>Choose how content in this section should be sorted</HelpText>
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button $primary onClick={handleSubmit} disabled={!canSubmit}>
            {editingSection ? 'Save Changes' : 'Create Section'}
          </Button>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default CustomSectionModal;
